import { isVoucherDeal } from "@/lib/is-voucher-deal";
import { isRoundtripCatalogDeal } from "@/lib/roundtrip";

/**
 * Price-history gate for every customer-facing discount claim.
 *
 * The old STRIKETHROUGH-STOP (taak #57 / AUDIT-F2, 2026-08-16) removed all
 * claims derived from free CMS/operator fields (`discount_percentage`,
 * `actual_price`, `lowest_price_before_discount`). Taak #47 re-opens only the
 * markets whose BFF payload now carries the per-deal `price_history_gate`
 * signal backed by P_min,30 history.
 *
 * `BEFORE_PRICE_VERIFIED_MARKETS` remains the explicit per-market activation
 * switch. Only add a market here once its backend data is actually populated
 * and its display rule is wired. The per-deal reader below still fails closed
 * for empty/missing data, so activating a market before the data is filled
 * renders exactly no claim; it must never fall back to legacy price fields.
 */
export const BEFORE_PRICE_VERIFIED_MARKETS = new Set([
  // Add a market only after its backend price_history_gate data is populated
  // and its market-specific display rule is wired. Per-deal gates still
  // fail closed, so empty data produces no visible claim.
  "nl-nl",
  "de-de",
]);

const PRICE_HISTORY_DISPLAY_RULE_BY_MARKET = {
  "nl-nl": "badge",
  "de-de": "absolute_amount",
};

// Round-3 review (Codex + independent, blocking): a P_min,30 claim this
// large is Twyman's law territory -- org-wide doctrine (see
// memory/project_dealfactory_grondwet.md, "Twyman >50%=meetfout") treats any
// discount over 50% as a measurement error, not a real deal, and the
// dealfactory-motor's own price-match gate enforces the identical ceiling
// (TWYMAN_MATCHED_CEILING_PCT=50). Applies here too: whatever the backend
// computed, a >50% claim on our OWN price history is more likely a data bug
// (stale/wrong pMin30Eur) than a genuine deal, and must not reach a customer.
const TWYMAN_CEILING_PCT = 50;

/** True only for a market taak #47 has explicitly activated. */
export function beforePriceSourceVerified(marketKey) {
  return BEFORE_PRICE_VERIFIED_MARKETS.has(marketKey);
}

export function priceHistoryDisplayRuleForMarket(marketKey) {
  if (!beforePriceSourceVerified(marketKey)) return null;
  return PRICE_HISTORY_DISPLAY_RULE_BY_MARKET[marketKey] || null;
}

function positiveNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

function floorToCents(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.floor(number * 100 + 1e-7) / 100;
}

function normalizeWindowDays(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : null;
}

/**
 * Return a normalized, display-safe price-history claim, or null.
 *
 * Backend contract:
 * - DE (`absolute_amount`): `pMin30Eur` + `savingsEur` must be positive.
 * - NL (`badge`): only `savingsPct` is required and displayed.
 *
 * Frontend rule: never round a claim upward. Percentages are floored and EUR
 * amounts are floored to cents before any caller formats/converts them.
 */
export function readPriceHistoryClaim(deal, marketKey, { currentPriceEur } = {}) {
  if (isVoucherDeal(deal)) return null;

  const expectedDisplayRule = priceHistoryDisplayRuleForMarket(marketKey);
  if (!expectedDisplayRule) return null;

  const gate = deal?.price_history_gate;
  if (!gate || gate.eligible !== true || gate.displayRule !== expectedDisplayRule) {
    return null;
  }

  if (expectedDisplayRule === "badge") {
    const savingsPctRaw = positiveNumber(gate.savingsPct);
    if (savingsPctRaw == null) return null;
    const savingsPct = Math.floor(savingsPctRaw);
    if (savingsPct <= 0) return null;
    if (savingsPctRaw > TWYMAN_CEILING_PCT) return null;
    return {
      displayRule: "badge",
      pMin30Eur: null,
      savingsEur: null,
      savingsPct,
      windowDays: normalizeWindowDays(gate.windowDays),
    };
  }

  if (expectedDisplayRule === "absolute_amount") {
    const pMin30Eur = floorToCents(positiveNumber(gate.pMin30Eur));
    const savingsEur = floorToCents(positiveNumber(gate.savingsEur));
    if (!(pMin30Eur > 0) || !(savingsEur > 0)) return null;
    if (savingsEur > pMin30Eur) return null;
    if ((savingsEur / pMin30Eur) * 100 > TWYMAN_CEILING_PCT) return null;

    const current = Number(currentPriceEur);
    if (Number.isFinite(current) && current > 0) {
      if (pMin30Eur <= current) return null;
      // Round-2 review (Codex, blocking): only checking savingsEur <=
      // pMin30Eur let an inflated backend savingsEur through as long as it
      // stayed under the reference price -- e.g. pMin30Eur=120, current=99,
      // savingsEur=119 (true delta is 21). Cross-check against the actual
      // floored price delta whenever the caller's own current price is
      // available; fail closed on any mismatch rather than silently
      // substituting a recomputed number.
      //
      // Round-3 review (non-blocking precision note): comparing floats
      // directly made the exactly-1-cent-off boundary non-deterministic
      // (21.01-21.00 and 50.00-49.99 both "look like" 1 cent but differ in
      // IEEE754 noise past the 15th digit). Compare whole cents as
      // integers instead -- eliminates the float-noise boundary entirely,
      // still fails closed on any real mismatch.
      const actualDelta = floorToCents(pMin30Eur - current);
      const savingsCents = Math.round(savingsEur * 100);
      const actualDeltaCents = Math.round(actualDelta * 100);
      if (savingsCents !== actualDeltaCents) return null;
    }

    return {
      displayRule: "absolute_amount",
      pMin30Eur,
      savingsEur,
      savingsPct: null,
      windowDays: normalizeWindowDays(gate.windowDays),
    };
  }

  return null;
}

/**
 * Read the explicit Favotrip list-price discount configured for a roundtrip.
 * This is deliberately separate from P_min,30 price-history claims. Only the
 * NL roundtrip portfolio may use it, only for the agreed 15-30% range, and
 * only when the backend reference price reconciles with the live sale price.
 */
export function readConfiguredRoundtripDiscountClaim(deal, marketKey, { currentPriceEur } = {}) {
  if (marketKey !== "nl-nl" || isVoucherDeal(deal) || !isRoundtripCatalogDeal(deal)) return null;

  const savingsPct = Number(deal?.discount_percentage ?? deal?.discountPercentage);
  if (!Number.isInteger(savingsPct) || savingsPct < 15 || savingsPct > 30) return null;

  const current = positiveNumber(currentPriceEur);
  const referencePriceEur = floorToCents(
    positiveNumber(deal?.lowest_price_calculated ?? deal?.lowestPriceCalculated),
  );
  if (!(current > 0) || !(referencePriceEur > current)) return null;

  const expectedReference = current / (1 - savingsPct / 100);
  if (Math.abs(referencePriceEur - expectedReference) > 0.011) return null;

  return {
    displayRule: "configured_percentage",
    pMin30Eur: null,
    referencePriceEur,
    savingsEur: null,
    savingsPct,
    windowDays: null,
  };
}

/** Prefer verified price history; otherwise allow the narrow roundtrip rule. */
export function readCustomerPriceClaim(deal, marketKey, options = {}) {
  return readPriceHistoryClaim(deal, marketKey, options)
    || readConfiguredRoundtripDiscountClaim(deal, marketKey, options);
}
